import { useMemo } from 'react'
import { Pressable } from '@react-aria/interactions'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { CalendarFilters } from 'screens/Calendar/types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { PostingOnMenu } from '../Toolbar/PostingOnMenu'
import { ChevronDown } from 'blaze-ui/icons'
import dayjs from 'dayjs'
import { Text } from 'blaze-ui/Text/Text'
import { QuickEditsSection } from './QuickEditsSection'
import { RedesignSection } from './RedesignSection'
import { ChannelList } from './ChannelList'
import { CampaignSection } from './CampaignSection'
import {
  filterGBPFromCrossPostGroup,
  getCrossPostGroupForChannel,
  isVideoChannel,
  isBlogChannel,
  isEmailChannel,
} from '../utils'
import { getEarliestPublishedAt } from 'hooks/queries/scheduledPosts/scheduledPostUtils'
import Styles from './Sidebar.module.scss'

export type ContentVariant = 'social' | 'shortFormVideo' | 'blog' | 'email'

const getContentVariant = (channel: string | undefined): ContentVariant => {
  if (channel && isVideoChannel(channel as DesignChannel)) return 'shortFormVideo'
  if (channel && isBlogChannel(channel)) return 'blog'
  if (channel && isEmailChannel(channel)) return 'email'
  return 'social'
}

interface SidebarProps {
  scheduledPost: ScheduledPostShow
  almanacFile: AlmanacFileShowItem
  previewPlatform?: ExternalIntegrationEnum
  activeIntegrationId?: number
  onSelectPlatform: (platform: ExternalIntegrationEnum, integrationId?: number) => void
  isBlankDesign: boolean
  onLoadingChange?: (isLoading: boolean) => void
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onDelete: (skipNavigation?: boolean) => void
  filters: CalendarFilters
  fileChannel: ChannelType | DesignChannel | GenerationType
  startDate?: string
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  isCurrentChannelUnsupported?: boolean
  onClose?: () => void
  isInsertingMedia?: boolean
}

export const Sidebar = ({
  scheduledPost,
  almanacFile,
  previewPlatform,
  activeIntegrationId,
  onSelectPlatform,
  isBlankDesign,
  onLoadingChange,
  isLoading,
  setIsLoading,
  onDelete,
  filters,
  fileChannel,
  startDate,
  onUpdateAlmanacFile,
  isCurrentChannelUnsupported = false,
  onClose,
  isInsertingMedia = false,
}: SidebarProps) => {
  const crossPostGroup = useMemo(
    () => filterGBPFromCrossPostGroup(getCrossPostGroupForChannel(fileChannel), true),
    [fileChannel],
  )

  const isPosted = almanacFile.post_status === PostStatusPropertyValue.Posted
  const targets = almanacFile?.scheduled_post_targets
  const hasAnyFailedTarget = targets?.some((t) => t.status === 'failed') ?? false
  const hasAnyPublishedTarget = targets?.some((t) => t.status === 'published') ?? false
  const getPostingOnTitle = () => {
    if (isPosted) return 'Posted on'
    if (hasAnyFailedTarget && !hasAnyPublishedTarget) return 'Not posted on'
    return 'Posting on'
  }
  const postingOnTitle = getPostingOnTitle()
  const contentVariant = getContentVariant(almanacFile.channel ?? undefined)

  const displayPostAt = useMemo(() => {
    if (isPosted && targets) {
      const earliestPublishedAt = getEarliestPublishedAt(targets)
      if (earliestPublishedAt) return earliestPublishedAt
    }
    return almanacFile?.post_at ?? null
  }, [isPosted, targets, almanacFile?.post_at])
  const formattedPostAt = displayPostAt ? dayjs(displayPostAt).format('ddd MMM D, h:mma') : null
  const scheduleLabel = formattedPostAt ?? (isPosted ? '—' : 'Schedule Post')

  return (
    <div className={Styles.sidebar}>
      <PostingOnMenu
        almanacFile={almanacFile}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        onDelete={() => onDelete(true)}
        filters={filters}
        fileChannel={fileChannel}
        startDate={startDate}
        onUpdateAlmanacFile={onUpdateAlmanacFile}
        buttonSize="md"
        showViewPostButton={almanacFile.channel === ChannelType.BlogPost && isPosted}
        trigger={(() => {
          const card = (
            <div className={isPosted ? Styles.scheduleCardStatic : Styles.scheduleCard}>
              <div className={Styles.sectionLabel}>
                <Text variant="placeholder" color="var(--dark-40)">
                  {postingOnTitle}
                </Text>
              </div>
              <div className={Styles.scheduleTriggerRow}>
                <Text variant="secondary" color="var(--dark-90)">
                  {scheduleLabel}
                </Text>
                {!isPosted && <ChevronDown size={12} color="var(--dark-40)" />}
              </div>
            </div>
          )
          return isPosted ? card : <Pressable>{card}</Pressable>
        })()}
      />

      {!isCurrentChannelUnsupported && (
        <>
          <div className={Styles.divider} />
          <div className={Styles.section}>
            <div className={Styles.sectionLabel}>
              <Text variant="placeholder" color="var(--dark-40)">
                Posting to
              </Text>
            </div>
            <ChannelList
              crossPostGroup={crossPostGroup}
              almanacFile={almanacFile}
              activeProvider={previewPlatform}
              activeIntegrationId={activeIntegrationId}
              onSelectPlatform={onSelectPlatform}
              onUpdateAlmanacFile={onUpdateAlmanacFile}
            />
          </div>
        </>
      )}

      {almanacFile.campaign_info && (
        <>
          <div className={Styles.divider} />
          <CampaignSection campaignInfo={almanacFile.campaign_info} onClose={onClose} />
        </>
      )}

      {!isPosted && (
        <>
          <div className={Styles.divider} />
          <div className={Styles.section}>
            <QuickEditsSection
              scheduledPost={scheduledPost}
              almanacFile={almanacFile}
              previewPlatform={previewPlatform}
              contentVariant={contentVariant}
              isBlankDesign={isBlankDesign}
              isInsertingMedia={isInsertingMedia}
              onClose={onClose}
            />
          </div>

          {(contentVariant === 'social' || contentVariant === 'shortFormVideo') && !isBlankDesign && (
            <>
              <div className={Styles.divider} />
              <div className={Styles.section}>
                <RedesignSection
                  almanacFile={almanacFile}
                  isInsertingMedia={isInsertingMedia}
                  onLoadingChange={onLoadingChange}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
